import React from 'react';
import {
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { TransactionType, TransactionCategory } from '@inversor-hub/types';
import { transactionTypeLabels, expenseCategories, incomeCategories } from './constants';
import { TransactionCategorySelect } from './TransactionCategorySelect';

interface TransactionFiltersProps {
  type?: TransactionType;
  category?: TransactionCategory;
  startDate?: string;
  endDate?: string;
  onTypeChange: (type?: TransactionType) => void;
  onCategoryChange: (category?: TransactionCategory) => void;
  onStartDateChange: (date?: string) => void;
  onEndDateChange: (date?: string) => void;
  onClear: () => void;
}

export const TransactionFilters: React.FC<TransactionFiltersProps> = ({
  type,
  category,
  startDate,
  endDate,
  onTypeChange,
  onCategoryChange,
  onStartDateChange,
  onEndDateChange,
  onClear,
}) => {
  const hasActiveFilters = type || category || startDate || endDate;

  const handleTypeChange = (newType?: TransactionType) => {
    onTypeChange(newType);
    // Clear category if it doesn't belong to the new type
    if (category && newType) {
      const categoriesForType = newType === 'EXPENSE' ? expenseCategories : incomeCategories;
      if (!categoriesForType.includes(category)) {
        onCategoryChange(undefined);
      }
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
        <FilterAltOutlinedIcon
          fontSize="small"
          sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}
        />

        <FormControl size="small" sx={{ width: { xs: '100%', sm: 150 } }}>
          <InputLabel>Tipo</InputLabel>
          <Select
            value={type || ''}
            onChange={(e) =>
              handleTypeChange((e.target.value as TransactionType | '') || undefined)
            }
            label="Tipo"
          >
            <MenuItem value="">
              <em>Todos</em>
            </MenuItem>
            {Object.entries(transactionTypeLabels).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TransactionCategorySelect
          value={category || ''}
          onChange={(nextCategory) => onCategoryChange(nextCategory || undefined)}
          type={type}
          includeAllOption
          allOptionLabel="Todas"
          size="small"
          fullWidth={false}
          sx={{ width: { xs: '100%', sm: 240 } }}
        />

        <Box
          display="flex"
          gap={1.5}
          alignItems="center"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <TextField
            type="date"
            label="Desde"
            size="small"
            value={startDate || ''}
            onChange={(e) => onStartDateChange(e.target.value || undefined)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: { xs: 1, sm: 'none' }, width: { sm: 150 } }}
          />

          <Box component="span" sx={{ color: 'text.disabled' }}>
            –
          </Box>

          <TextField
            type="date"
            label="Hasta"
            size="small"
            value={endDate || ''}
            onChange={(e) => onEndDateChange(e.target.value || undefined)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: { xs: 1, sm: 'none' }, width: { sm: 150 } }}
          />
        </Box>

        {hasActiveFilters && (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: 'none', sm: 'block' }, ml: 'auto' }}
            />
            <Button
              startIcon={<ClearIcon />}
              onClick={onClear}
              size="small"
              color="inherit"
              sx={{ color: 'text.secondary', width: { xs: '100%', sm: 'auto' } }}
            >
              Limpiar filtros
            </Button>
          </>
        )}
      </Box>
    </Paper>
  );
};
